// src/utils/fs-utils.ts - Browser-Safe File System Utilities (StackBlitz Compatible)

/**
 * Browser-safe file system utilities (works in Node.js environment)
 * StackBlitz compatible with WebContainer support
 */

// Cache fs module to avoid multiple requires
let fsModule: any = null;
let fsInitialized = false;

/**
 * Initialize fs module (lazy loading)
 */
function initializeFs(): boolean {
    if (fsInitialized) {
        return fsModule !== null;
    }

    fsInitialized = true;

    // Browser environment check
    if (typeof window !== 'undefined') {
        console.warn('[XCon Plugin] File system access not available in browser environment');
        return false;
    }

    try {
        // Try to require fs module
        fsModule = require('fs');
        return true;
    } catch (e) {
        console.warn('[XCon Plugin] fs module not available:', e);
        return false;
    }
}

export const fsUtils = {
    /**
     * Check if file exists (Node.js only)
     */
    existsSync: (filePath: string): boolean => {
        if (!initializeFs()) {
            return false;
        }

        try {
            return fsModule.existsSync(filePath);
        } catch (e) {
            console.warn(`[XCon Plugin] Failed to check file existence: ${filePath}`, e);
            return false;
        }
    },

    /**
     * Read file content synchronously (Node.js only)
     */
    readFileSync: (filePath: string, encoding: BufferEncoding = 'utf8'): string => {
        if (!initializeFs()) {
            return '';
        }

        try {
            return fsModule.readFileSync(filePath, encoding);
        } catch (e) {
            console.warn(`[XCon Plugin] Failed to read file: ${filePath}`, e);
            return '';
        }
    },

    /**
     * Get file stats (Node.js only)
     */
    statSync: (filePath: string): any | null => {
        if (!initializeFs()) {
            return null;
        }

        try {
            return fsModule.statSync(filePath);
        } catch (e) {
            console.warn(`[XCon Plugin] Failed to get file stats: ${filePath}`, e);
            return null;
        }
    },

    /**
     * Check if path is a directory (Node.js only)
     */
    isDirectory: (filePath: string): boolean => {
        const stats = fsUtils.statSync(filePath);
        return stats ? stats.isDirectory() : false;
    },

    /**
     * Check if path is a file (Node.js only)
     */
    isFile: (filePath: string): boolean => {
        const stats = fsUtils.statSync(filePath);
        return stats ? stats.isFile() : false;
    },

    /**
     * Check if running in Node.js environment
     */
    isNodeEnvironment: (): boolean => {
        return typeof window === 'undefined' &&
            typeof process !== 'undefined' &&
            process.versions != null &&
            process.versions.node != null;
    },

    /**
     * Safe file read with fallback
     */
    safeReadFile: (filePath: string, fallback: string = ''): string => {
        if (!fsUtils.isNodeEnvironment()) {
            return fallback;
        }

        if (fsUtils.existsSync(filePath)) {
            return fsUtils.readFileSync(filePath);
        }

        return fallback;
    },

    /**
     * List files in directory (Node.js only)
     */
    readdirSync: (dirPath: string): string[] => {
        if (!initializeFs()) {
            return [];
        }

        try {
            return fsModule.readdirSync(dirPath);
        } catch (e) {
            console.warn(`[XCon Plugin] Failed to read directory: ${dirPath}`, e);
            return [];
        }
    }
};